import { useCallback, useEffect, useState } from 'react'
import Icon from '../components/Icon'
import { errorText, getLocale } from '../lib/i18n'
import { post } from '../lib/api'
import { AREAS } from '../../shared/market/model'
import { getSpots, mapsUrl, type Spot } from './api'

/**
 * For the team: the official meeting places. Each one belongs to a general area, so "Public place" on an ad suggests the
 * right one. Add one by standing there ("Use my location"), or paste a Google Maps link or "18.1491, -65.4413".
 */
const L = (en: string, es: string) => (getLocale() === 'en' ? en : es)
/** Two decimal numbers from pasted text: coordinates, or a Google Maps link that has them. */
export const parseLatLng = (text: string): { lat: number; lng: number } | null => {
  const m = text.match(/(-?\d{1,3}\.\d{3,})\s*[,\s]\s*(-?\d{1,3}\.\d{3,})/)
  return m ? { lat: Number(m[1]), lng: Number(m[2]) } : null
}

export default function SpotsAdmin() {
  const [spots, setSpots] = useState<Spot[]>([]), [name, setName] = useState(''), [area, setArea] = useState<string>(AREAS[0]), [where, setWhere] = useState('')
  const [busy, setBusy] = useState(false), [error, setError] = useState('')
  const load = useCallback(() => { void getSpots().then(r => setSpots(r.spots.filter(s => s.official))).catch(() => undefined) }, [])
  useEffect(() => { load() }, [load])
  const point = parseLatLng(where)
  const here = () => {
    setError('')
    if (!navigator.geolocation) { setError(L('This device cannot share its location.', 'Este dispositivo no puede compartir su ubicación.')); return }
    navigator.geolocation.getCurrentPosition(p => setWhere(`${p.coords.latitude.toFixed(6)}, ${p.coords.longitude.toFixed(6)}`), () => setError(L('Could not get your location. Paste the coordinates instead.', 'No se pudo obtener tu ubicación. Pega las coordenadas.')), { enableHighAccuracy: true, timeout: 15000 })
  }
  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!point || name.trim().length < 2 || busy) return
    setBusy(true); setError('')
    try { await post('/api/market/spots', { name: name.trim(), area, lat: point.lat, lng: point.lng }); setName(''); setWhere(''); load() }
    catch (err) { setError(errorText(err)) } finally { setBusy(false) }
  }
  const remove = async (s: Spot) => {
    if (!confirm(L(`Remove ${s.name}?`, `¿Quitar ${s.name}?`))) return
    try { await post(`/api/market/spots/${s.id}/remove`, {}); load() } catch (err) { setError(errorText(err)) }
  }
  return <section className="spots-admin" aria-labelledby="spots-title">
    <h2 id="spots-title"><Icon name="pin" size={20}/>{L('Meeting places', 'Puntos de encuentro')}</h2>
    <p>{L('Official public places, one per area. A "Public place" pickup suggests the one for its area.', 'Lugares públicos oficiales, uno por área. Un encuentro en "Lugar público" sugiere el de su área.')}</p>
    <ul className="spots-list">
      {spots.map(s => <li key={s.id}><span><strong>{s.name}</strong> <small>{s.area ?? L('any area', 'cualquier área')}</small></span><a href={mapsUrl(s.lat, s.lng)} target="_blank" rel="noopener noreferrer">{L('Map', 'Mapa')}</a><button type="button" className="market-link" onClick={() => void remove(s)}>{L('Remove', 'Quitar')}</button></li>)}
      {!spots.length && <li><small>{L('None yet.', 'Ninguno todavía.')}</small></li>}
    </ul>
    <form onSubmit={e => void add(e)} className="spots-form">
      <label>{L('Name', 'Nombre')}<input value={name} maxLength={60} onChange={e => setName(e.target.value)} placeholder={L('Isabel II plaza', 'Plaza de Isabel II')}/></label>
      <label>{L('Area', 'Área')}<select value={area} onChange={e => setArea(e.target.value)}>{AREAS.map(a => <option key={a} value={a}>{a}</option>)}</select></label>
      <label>{L('Location', 'Ubicación')}<input value={where} onChange={e => setWhere(e.target.value)} placeholder="18.1491, -65.4413"/></label>
      <div className="spots-actions"><button type="button" className="market-button secondary" onClick={here}><Icon name="pin" size={18}/>{L('Use my location', 'Usar mi ubicación')}</button><button type="submit" className="market-button" disabled={!point || name.trim().length < 2 || busy}>{busy ? '…' : L('Add place', 'Agregar')}</button></div>
      {where && !point && <small className="error">{L('Paste two coordinates or a Google Maps link.', 'Pega dos coordenadas o un enlace de Google Maps.')}</small>}
      {error && <p role="alert" className="error">{error}</p>}
    </form>
  </section>
}
